import { useState } from 'react';

import { productionApi } from 'api/production';
import ColorInput from 'components/storefront/StorefrontConfigEditor/components/ColorInput';
import ImageInput from 'components/storefront/StorefrontConfigEditor/components/ImageInput';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { CONTENT_LINK_URL_PATTERN, CONTENT_LINK_URL_TITLE } from 'shared/utils/contentLinkUrl';

/**
 * Default configuration for the Banner1 item
 *
 * @constant {Object}
 * @property {string} bgColor - Background color in hex format
 * @property {string} text - Banner text content
 * @property {string} imageLeftUrl - Left image URL
 * @property {string} imageRightUrl - Right image URL
 * @property {string} linkUrl - Banner link URL
 * @property {boolean} openLinkInNewTab - Whether to open link in new tab
 */
export const DEFAULT_BANNER_ITEM = Object.freeze({
    bgColor: '#FFFFFF',
    text: '',
    imageLeftUrl: '',
    imageRightUrl: '',
    linkUrl: '',
    openLinkInNewTab: true,
});

/**
 * Generic Banner 1 form component.
 *
 * Renders the form fields for a single banner with text, two accent images,
 * a link, and a background color. This component is context-agnostic and can
 * be used in both the StorefrontConfigEditor and the AisleContent editor.
 *
 * @param {Object} props
 * @param {Object} props.data - Banner data object (must include `id`, `name`, `items`)
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Function} props.onChange - Callback function for handling data changes
 * @returns {JSX.Element} Banner1Form component
 */
export default function Banner1Form({ data, disabled, onChange }) {
    const [imageErrorLeft, setImageErrorLeft] = useState(null);
    const [imageErrorRight, setImageErrorRight] = useState(null);

    const handleChange = (e) => {
        onChange({ ...data, items: [{ ...data.items[0], [e.target.name]: e.target.value }] });
    };

    const handleNameChange = (e) => {
        onChange({ ...data, name: e.target.value });
    };

    const handleImageLeftUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ ...data, items: [{ ...data.items[0], imageLeftUrl: imageUrl }] });
            setImageErrorLeft(null);
        } catch (e) {
            setImageErrorLeft(`Error: ${e.message}`);
        }
    };

    const handleImageRightUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ ...data, items: [{ ...data.items[0], imageRightUrl: imageUrl }] });
            setImageErrorRight(null);
        } catch (e) {
            setImageErrorRight(`Error: ${e.message}`);
        }
    };

    const handleImageLeftClear = () => {
        onChange({ ...data, items: [{ ...data.items[0], imageLeftUrl: '' }] });
    };

    const handleImageRightClear = () => {
        onChange({ ...data, items: [{ ...data.items[0], imageRightUrl: '' }] });
    };

    const handleCheckboxChange = (checked) => {
        onChange({ ...data, items: [{ ...data.items[0], openLinkInNewTab: checked }] });
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
                <Label htmlFor={`name-${data.id}`}>Name</Label>
                <p className="text-xs text-muted-foreground -mt-0.5">
                    For internal reference only. Not displayed to customers.
                </p>
                <Input
                    type="text"
                    id={`name-${data.id}`}
                    name="name"
                    value={data.name ?? ''}
                    placeholder="Enter banner name"
                    onChange={handleNameChange}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`text-${data.id}`}>Text</Label>
                <Input
                    type="text"
                    id={`text-${data.id}`}
                    name="text"
                    value={data.items[0].text ?? ''}
                    placeholder="Enter banner text"
                    onChange={handleChange}
                    maxLength={55}
                    required
                    disabled={disabled}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <ImageInput
                    id={`imageLeft-${data.id}`}
                    imageUrl={data.items[0].imageLeftUrl}
                    error={imageErrorLeft}
                    onClearImage={handleImageLeftClear}
                    onImageUpload={handleImageLeftUpload}
                    disabled={disabled}
                />
                <ImageInput
                    id={`imageRight-${data.id}`}
                    imageUrl={data.items[0].imageRightUrl}
                    error={imageErrorRight}
                    onClearImage={handleImageRightClear}
                    onImageUpload={handleImageRightUpload}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`linkUrl-${data.id}`}>Link URL</Label>
                <Input
                    type="text"
                    id={`linkUrl-${data.id}`}
                    name="linkUrl"
                    value={data.items[0].linkUrl ?? ''}
                    placeholder="Enter link URL"
                    onChange={handleChange}
                    disabled={disabled}
                    pattern={CONTENT_LINK_URL_PATTERN}
                    title={CONTENT_LINK_URL_TITLE}
                />
            </div>

            <div className="col-span-2 flex items-center justify-end gap-2">
                <Checkbox
                    id={`openLinkInNewTab-${data.id}`}
                    name="openLinkInNewTab"
                    checked={data.items[0].openLinkInNewTab}
                    onCheckedChange={handleCheckboxChange}
                    className="h-5 w-5"
                    disabled={disabled}
                />
                <Label className="font-normal" htmlFor={`openLinkInNewTab-${data.id}`}>
                    Open Link in New Tab
                </Label>
            </div>

            <ColorInput
                id={`bgColor-${data.id}`}
                label="Background Color"
                name="bgColor"
                value={data.items[0].bgColor}
                onChange={handleChange}
                disabled={disabled}
            />
        </div>
    );
}
